import React, { useCallback, useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useToast, useDisclosure, VStack } from "@chakra-ui/react";
import { Formik } from "formik";
import { Route, Switch, useRouteMatch, useHistory } from "react-router-dom";

import { useOverlaySpinner } from "components/OverlaySpinner";
import { IframeOrFullPageWrapper } from "components/Container";
import { useSubmitKYC } from "api/kyc";
import { useUpdateAccountInfo } from "api/accounts/index.js";
import Success from "../Success";

import FirstHalf from "./FirstHalf";
import AdditionalDocuments from "./AdditionalDocuments";
import PostSubmissionPolling from "./PostSubmissionPolling";
import getKYCSchema from "../schema";
import { formatFormDataForApi, formatFormDataForTaxUpdate, getInitialValues } from "../utils";
import RetryModal from "./RetryModal";
import Header from "flows/components/Header";
import { useAnalytics } from "utils";

/**
 * KYC2Form
 * @param {KYC2Props} props
 * @returns JSX.Element
 * @todo Rename this to KYC when old KYC has been fully deprecated
 */
export default function KYC2Form({
  onComplete,
  onError,
  config,
  limitToBasicPII = false,
  condition,
  longSubmissionTimeoutDuration = 60000, // as param to make testing easier
}) {
  const { formatMessage } = useIntl();
  const [mostRecentSubmittedFormValues, setMostRecentSubmittedFormValues] = useState(null);
  const [mostRecentSubmittedTouchedFields, setMostRecentSubmittedTouchedFields] = useState(null);
  const [retryMessage, setRetryMessage] = useState(null);
  const { showOverlaySpinner, hideOverlaySpinner } = useOverlaySpinner();
  const toast = useToast();
  const history = useHistory();
  const analytics = useAnalytics();
  const {
    isOpen: isRetryModalOpen,
    onOpen: onOpenRetryModal,
    onClose: onCloseRetryModal,
  } = useDisclosure();
  const longSubmitCheckTimer = useRef(null);
  const dataFormatingHasCompleted = useRef(false); // used to help track issue where formatting the form data before sending (including compressing images, which is likely main issue) can take longer than it should (could be because of memory issues on device)

  const { path } = useRouteMatch();
  const {
    mutateAsync: submitKYC,
    data: kycMeta,
    isSuccess,
    reset: resetSubmitKYC,
  } = useSubmitKYC();

  const {
    mutateAsync: updateAccountInfo,
    isSuccess: isUpdateAccountInfoSuccess,
    reset: resetUpdateAccountInfo,
  } = useUpdateAccountInfo();

  const KYCSchema = getKYCSchema(config, formatMessage, condition);
  const initialValues = getInitialValues({ limitToBasicPII });

  const fireSubmitClickedEvent = useCallback(
    (jsonValues) => {
      let size = null;
      try {
        const jsonStr = JSON.stringify(jsonValues);
        size = new Blob([jsonStr]).size;
      } catch {
        // no op, is this try even needed?  Just playing safe for the < 2% of browsers that may not support blob
      }
      analytics.track("KYC_FormSubmit_Clicked", { payload_size_bytes: size });
    },
    [analytics]
  );

  /**
   * useEffect to handle clearing a timeout
   * for handleLongSubmitCheckTimeout if still there.
   */
  useEffect(() => {
    return () => {
      if (longSubmitCheckTimer.current) {
        clearTimeout(longSubmitCheckTimer.current);
      }
    };
  }, []);

  /**
   * handleLongSubmitCheckTimeout
   * We want to watch for any submissions that may
   * be taking an extreme amount of time which could
   * be a result of a poor connect, a large payload,
   * or both.
   */
  const handleLongSubmitCheckTimeout = useCallback(() => {
    if (longSubmitCheckTimer.current) {
      clearTimeout(longSubmitCheckTimer.current);
    }
    longSubmitCheckTimer.current = setTimeout(() => {
      analytics.track("KYC_LongSubmit_Timeout_Reached", {
        timeoutDuration: longSubmissionTimeoutDuration,
        dataFormatingHasCompleted: dataFormatingHasCompleted.current,
      });
      const desc = dataFormatingHasCompleted.current ? formatMessage({
        defaultMessage:
          "It looks like you may be on a slow connection.  We are still trying to submit your application, but you may need to submit again when on a faster network connection.",
        description: "Description to user they may be on a slow connection",
      }) : `${formatMessage({
        defaultMessage:
          "Please contact support.",
        description: "Description to user they should contact support",
      })} ${config.support_url || "https://www.tilia.io/support"}`;
      toast({
        title: formatMessage({
          defaultMessage: "This seems to be taking a while...",
          description: "Title about form submission taking a long time",
        }),
        description: desc,
        status: "warning",
        duration: 10000,
        isClosable: true,
      });
    }, longSubmissionTimeoutDuration);
  }, [toast, formatMessage, analytics, longSubmissionTimeoutDuration, dataFormatingHasCompleted, config]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        showOverlaySpinner();
        dataFormatingHasCompleted.current = false; // init to false as we have yet to process data
        handleLongSubmitCheckTimeout();
        if (condition === "TAX_UPDATE") {
          const formData = await formatFormDataForTaxUpdate(values);
          dataFormatingHasCompleted.current = true;
          fireSubmitClickedEvent(formData);
          await updateAccountInfo(formData);
        } else {
          const formData = await formatFormDataForApi(values);
          dataFormatingHasCompleted.current = true;
          fireSubmitClickedEvent(formData);
          await submitKYC(formData);
        }
        setMostRecentSubmittedFormValues(values);
      } catch (error) {
        const KYC_SUBMIT_ERROR_CODES = {
          KYC_UNSUPPORTED_COUNTRY: formatMessage({
            defaultMessage: "This feature is not supported in your country",
            description: "Sanctioned country IP address error message",
          }),
        };
        const errCodeMsg =
          error.apiCodes &&
          error.apiCodes.length > 0 &&
          error.apiCodes.map((c) => KYC_SUBMIT_ERROR_CODES[c]).join(", ");
        const defaultDesc = formatMessage({
          defaultMessage:
            "So sorry, but we are unable to submit your identity verification at this time. Please try again later or contact support.",
          description: "Popup message",
        });
        analytics.track("KYC_Submit_Error", {
          error: error.message || errCodeMsg || defaultDesc,
          dataFormatingHasCompleted: dataFormatingHasCompleted.current,
        });
        toast({
          title: formatMessage({
            defaultMessage: "Unable to submit identity verification",
            description: "Popup title",
          }),
          description: errCodeMsg || defaultDesc,
          status: "error",
          isClosable: true,
        });
        if (onError) onError(error);
      } finally {
        if (longSubmitCheckTimer.current) {
          // clear timeout if still there
          clearTimeout(longSubmitCheckTimer.current);
        }
        hideOverlaySpinner();
      }

      return true;
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [formatMessage]
  ); // to get translated error codes we need to depend at least on this function

  const handleCloseRetryModal = useCallback(() => {
    onCloseRetryModal();
  }, [onCloseRetryModal]);

  /**
   * showRetryErrMessageHandler
   * If the user has submited their application and received
   * a retry error code, this function is to display an error
   * message when they try to submit the same data as last time
   * (user has to change some value of the form before we allow
   * them to submit again)
   */
  const showRetryErrMessageHandler = useCallback(() => {
    if (retryMessage) {
      toast({
        title: formatMessage({
          defaultMessage: "There was an issue.",
          description: "Popup title",
        }),
        description: retryMessage,
        status: "error",
        isClosable: true,
      });
    }
  }, [retryMessage, toast, formatMessage]);

  /**
   * retryRequiredHandler
   * To handle when we get a retry-able error code after
   * submitting application
   */
  const retryRequiredHandler = useCallback(
    (retryMsg = "Unknown error") => {
      if (history.location?.pathname.includes("/documents")) {
        history.push(`${path}`);
      }
      setRetryMessage(retryMsg);
      onOpenRetryModal();
      resetSubmitKYC();
      resetUpdateAccountInfo();
    },
    [history, onOpenRetryModal, resetSubmitKYC, resetUpdateAccountInfo, path]
  );

  /**
   * updateTouchedFieldsHandler
   * After a user submits the form, if they receive
   * a retry error, we want to rehydrate the values
   * and have all the same fields 'touched' to allow
   * re-submition.  Here we keep track of touched fields
   * when necessary.
   */
  const updateTouchedFieldsHandler = useCallback((touched) => {
    setMostRecentSubmittedTouchedFields(touched);
  }, []);

  if (isSuccess || isUpdateAccountInfoSuccess) {
    if (limitToBasicPII) {
      return <Success applicationState={"BASIC_PII_COLLECTED"} onComplete={onComplete} />;
    } else if (condition === "TAX_UPDATE") {
      return <Success applicationState={"TAX_UPDATE_COLLECTED"} onComplete={onComplete} />;
    } else {
      return (
        <PostSubmissionPolling
          kycId={kycMeta?.payload?.kyc_id}
          onComplete={onComplete}
          onError={onError}
          onRetryRequired={retryRequiredHandler}
        />
      );
    }
  }

  return (
    <Formik
      initialValues={mostRecentSubmittedFormValues || initialValues}
      initialTouched={mostRecentSubmittedTouchedFields || {}}
      validationSchema={KYCSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      <IframeOrFullPageWrapper>
        <Switch>
          <Route exact path={path}>
            <VStack spacing="3" align="stretch">
              <Header
                showLanguage
                title={formatMessage({
                  defaultMessage: "KYC Form",
                  description: "Page heading",
                })}
              />
              <FirstHalf
                onUpdateTouchedFields={updateTouchedFieldsHandler}
                onShowRetryErrMessage={showRetryErrMessageHandler}
                isRetry={!!retryMessage}
                condition={condition}
              />
            </VStack>
          </Route>
          <Route exact path={`${path}/documents`}>
            <VStack spacing="3" align="stretch">
              <Header
                showLanguage
                title={formatMessage({
                  defaultMessage: "KYC Form",
                  description: "Page heading",
                })}
              />
              <AdditionalDocuments
                config={config}
                onUpdateTouchedFields={updateTouchedFieldsHandler}
                onShowRetryErrMessage={showRetryErrMessageHandler}
                isRetry={!!retryMessage}
              />
            </VStack>
          </Route>
        </Switch>
        <RetryModal
          isOpen={isRetryModalOpen}
          onClose={handleCloseRetryModal}
          retryMessage={retryMessage}
        />
      </IframeOrFullPageWrapper>
    </Formik>
  );
}
